import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { jsonStorage } from "./jsonStorage";
import type { CartItem, CartValues } from "./types";

export const cartAtom = atomWithStorage<CartValues>(
  "user-cart",
  {
    cartItemsById: new Map(),
  },
  jsonStorage,
  { getOnInit: true },
);

export const useCartAtom = () => useAtom(cartAtom);

export const useCart = () => {
  const [cart, setCart] = useCartAtom();

  const addCartItem = (cartItem: CartItem) => {
    if (!cartItem?.id) return;

    // prevent override of existing listing
    if (cart?.cartItemsById?.has(`${cartItem?.id}`)) return;

    void setCart((cart) => {
      // FIXME: MVP constraint - change it back when we support more than 1 cart item
      // const cartItemsById = new Map(cart.listingsById);
      const cartItemsById = new Map<CartItem["id"], CartItem>();

      cartItemsById.set(`${cartItem?.id}`, cartItem);

      return { ...cart, cartItemsById };
    });
  };

  const removeCartItem = (cartItemId: string | number | undefined) => {
    if (!cartItemId) return;
    if (!cart?.cartItemsById?.has(`${cartItemId}`)) return;

    void setCart((cart) => {
      const cartItemsById = new Map<CartItem["id"], CartItem>(cart?.cartItemsById);

      cartItemsById.delete(`${cartItemId}`);

      return { ...cart, cartItemsById };
    });
  };

  const updateCartItem = (updatedCartItem: Partial<CartItem>) => {
    if (!updatedCartItem?.id) return;

    const existingCartItem = cart?.cartItemsById?.get(`${updatedCartItem.id}`);

    if (!existingCartItem) return;

    void setCart((cart) => {
      const cartItemsById = new Map<CartItem["id"], CartItem>(cart?.cartItemsById);

      const newCartItem = {
        ...existingCartItem,
        ...updatedCartItem,
      };

      if (updatedCartItem) {
        // @ts-expect-error Typings
        listingsById.set(updatedCartItem.id, newCartItem);
      }

      return { ...cart, cartItemsById };
    });
  };

  const clearCart = () => {
    void setCart((cart) => ({ ...cart, cartItemsById: new Map() }));
  };

  return {
    // readable state
    cart,

    // actions
    addCartItem,
    removeCartItem,
    updateCartItem,

    clearCart,
  };
};
